import { usePeriodsQuery } from '@/app/services/periodApi';
import { formatDateMonthYear } from '@/app/utils/dateUtils';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Card, CardContent, MenuItem, Select, Typography, useTheme } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '../common/StatusBadge';

function PeriodDetails() {
  const { t } = useTranslation(['periodDetails', 'common']);
  const theme = useTheme();

  const { selectedPeriod, setSelectedPeriod } = usePeriod();

  const { data } = usePeriodsQuery();

  const sortedData = useMemo(() => {
    return data?.slice().sort((a, b) => b.date.localeCompare(a.date));
  }, [data]);

  return (
    <Card
      sx={{
        boxShadow: 3,
        display: 'flex',
        minHeight: theme.spacing(9),
        alignItems: 'center',
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          maxHeight: theme.spacing(3),
          alignItems: 'center',
          pt: theme.spacing(2.75),
          width: '100%',
        }}
      >
        <Typography
          sx={{
            mt: theme.spacing(0.5),
            ml: theme.spacing(3),
            fontSize: theme.spacing(1.75),
            fontWeight: 'bold',
            color: '#4B4B4B',
          }}
          component="p"
          variant="fieldHeader"
        >
          {t('current.period')}
        </Typography>

        <FormControl variant="standard" sx={{ m: theme.spacing(1), minWidth: theme.spacing(15) }}>
          <Select
            value={selectedPeriod?.date || ''}
            size="small"
            sx={{
              width: theme.spacing(22.5),
              fontSize: theme.spacing(1.625),
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: theme.spacing(40),
                  width: theme.spacing(22.5),
                },
              },
            }}
            onChange={(event) => {
              const selectedDate = event.target.value;
              if (selectedDate) {
                const newSelectedPeriod = sortedData?.find((p) => p.date === selectedDate);
                if (newSelectedPeriod) {
                  setSelectedPeriod(newSelectedPeriod);
                }
              }
            }}
          >
            {(sortedData || []).map((period) => (
              <MenuItem
                sx={{
                  fontSize: theme.spacing(1.625),
                  fontWeight: period.date === selectedPeriod?.date ? 'bold' : 'normal',
                  color: period.date === selectedPeriod?.date ? '#F97C06' : '#4B4B4B',
                }}
                key={period.date}
                value={period.date}
              >
                {formatDateMonthYear(period.date)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: theme.spacing(7), ml: theme.spacing(5) }}>
          <StatusBadge
            label={`${t('common:processing')}`}
            importedData={selectedPeriod?.date as string}
            isActive={selectedPeriod?.processedStatus}
          />
          <StatusBadge
            label={`${t('common:annex.8')}`}
            isActive={selectedPeriod?.financialReport.imported as boolean}
            importedData={selectedPeriod?.date as string}
          />
          <StatusBadge
            label={`${t('common:ledger')}`}
            isActive={selectedPeriod?.financialData.imported as boolean}
            importedData={selectedPeriod?.financialData.lastImportDate as string}
          />
          <StatusBadge
            label={`${t('common:transport')}`}
            isActive={selectedPeriod?.transportRecife.imported as boolean}
            importedData={selectedPeriod?.transportRecife.lastImportDate as string}
          />
          <StatusBadge
            label={`${t('common:collaborator')}`}
            isActive={selectedPeriod?.employees.imported as boolean}
            importedData={selectedPeriod?.employees.lastImportDate as string}
          />
          <StatusBadge
            label={`${t('common:sharing')}`}
            isActive={selectedPeriod?.shared.imported as boolean}
            importedData={selectedPeriod?.shared.lastImportDate as string}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default PeriodDetails;
